import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BGV_ACTIVE, getBgv, saveBgvSettings } from "@/lib/bgvApi";
import { safeApiError } from "@/lib/safeErrorText";

const SAVE_MS = 700;
const POLL_MS = 2500;

/** One Background Singers session: polls while working, keeps your settings and auto-saves them as you move things. */
export default function useBgvSession(id) {
  const [doc, setDoc] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const timer = useRef(null);
  const latest = useRef(null);
  const prev = useRef(null);
  const edits = useRef({ made: 0, saved: 0 });
  latest.current = settings;

  const adopt = useCallback((d) => {
    const was = prev.current;
    prev.current = d;
    if (was?.status === "saving" && d.status === "ready" && d.saved && d.saved.song_id !== was.saved?.song_id) toast.success("Saved to your library", { description: d.saved.title });
    if (was?.status === "saving" && d.save_error) toast.error("Couldn't save the song", { description: d.save_error });
    if (was?.choir?.status === "running" && d.choir?.status === "done") toast.success("The AI choir is ready");
    setDoc(d);
    if (edits.current.made === edits.current.saved) setSettings(d.settings);
  }, []);

  const refresh = useCallback(async () => {
    try { adopt(await getBgv(id)); setError(null); } catch (e) { setError(e); }
  }, [id, adopt]);
  useEffect(() => { refresh(); }, [refresh]);

  const busy = Boolean(doc && (BGV_ACTIVE.includes(doc.status) || doc.choir?.status === "running" || doc.choir?.status === "queued"));
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [busy, refresh]);

  const persist = useCallback(async () => {
    timer.current = null;
    const seq = edits.current.made;
    try {
      const d = await saveBgvSettings(id, latest.current);
      edits.current.saved = Math.max(edits.current.saved, seq);
      adopt(d);
      setSaveState(edits.current.made === seq ? "saved" : "saving");
    }
    catch (e) { setSaveState("error"); toast.error("Couldn't save that change", { description: safeApiError(e) }); }
  }, [id, adopt]);
  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); saveBgvSettings(id, latest.current).catch(() => {}); } }, [id]);

  const change = useCallback((fn) => {
    setSettings((s) => { const next = fn(s); latest.current = next; return next; });
    edits.current.made += 1;
    setSaveState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(persist, SAVE_MS);
  }, [persist]);

  /** Save pending changes now (before Make / Save so the server sees them). */
  const flush = useCallback(async () => { if (timer.current) { clearTimeout(timer.current); await persist(); } }, [persist]);
  return { doc, settings, error, saveState, busy, change, flush, refresh, adopt };
}
